import type { Square, Position } from '@draughts/shared';

/**
 * Converte un numero di casella in coordinate SVG
 * Supporta sia 10x10 (International) che 8x8 (Italian)
 *
 * @param square Numero casella (1-50 per 10x10, 1-32 per 8x8)
 * @param squareSize Dimensione di una casella in pixel SVG
 * @param boardSize Dimensione scacchiera (10 o 8, default 10)
 * @returns Coordinate {x, y} del centro della casella in SVG
 */
export function squareToSVGCoords(
  square: Square,
  squareSize: number,
  boardSize: number = 10,
  darkSquareBottomRight: boolean = boardSize === 8,
  allSquaresPlayable: boolean = false
): { x: number; y: number } {
  const pos = squareToPosition(square, boardSize, darkSquareBottomRight, allSquaresPlayable);

  // Converti row/col (1-indexed) a coordinate SVG (0-indexed)
  // Row 1 è in alto, quindi y = (row - 1) * squareSize
  // Col 1 è a sinistra, quindi x = (col - 1) * squareSize
  const x = (pos.col - 1) * squareSize + squareSize / 2;
  const y = (pos.row - 1) * squareSize + squareSize / 2;

  return { x, y };
}

/**
 * Converte un numero di casella in Position (row, col)
 * Supporta sia 10x10 (International, 1-50) che 8x8 (Italian, 1-32)
 *
 * ORIENTAMENTO:
 * - International (10x10): casella scura in basso-SINISTRA
 * - Italian (8x8): casella scura in basso-DESTRA
 *
 * @param square Numero casella
 * @param boardSize Dimensione scacchiera (10 o 8, default 10)
 */
export function squareToPosition(square: Square, boardSize: number = 10, darkSquareBottomRight: boolean = boardSize === 8, allSquaresPlayable: boolean = false): Position {
  // All-squares numbering (Turkish/Dameo/Armenian/Croda): 1..N² row-major, every square playable
  if (allSquaresPlayable) {
    const maxSquares = boardSize * boardSize;
    if (square < 1 || square > maxSquares) {
      throw new Error(`Invalid square: ${square}. Must be 1-${maxSquares}`);
    }
    const row = Math.ceil(square / boardSize);
    const col = ((square - 1) % boardSize) + 1;
    return { row, col };
  }

  const squaresPerRow = boardSize / 2;
  const maxSquares = boardSize * squaresPerRow;

  if (square < 1 || square > maxSquares) {
    throw new Error(`Invalid square: ${square}. Must be 1-${maxSquares}`);
  }

  const row = Math.ceil(square / squaresPerRow);
  const posInRow = ((square - 1) % squaresPerRow);
  const isItalian = darkSquareBottomRight;

  let col: number;
  if (isItalian) {
    // Italian: righe dispari → colonne dispari, righe pari → colonne pari
    col = row % 2 === 1 ? posInRow * 2 + 1 : posInRow * 2 + 2;
  } else {
    // International: righe dispari → colonne pari, righe pari → colonne dispari
    col = row % 2 === 1 ? posInRow * 2 + 2 : posInRow * 2 + 1;
  }

  return { row, col };
}

/**
 * Converte Position (row, col) in numero di casella
 * Ritorna null se la casella non è giocabile (casella chiara)
 *
 * @param pos Position {row, col}
 * @param boardSize Dimensione scacchiera (10 o 8, default 10)
 */
export function positionToSquare(pos: Position, boardSize: number = 10, darkSquareBottomRight: boolean = boardSize === 8, allSquaresPlayable: boolean = false): Square | null {
  const { row, col } = pos;

  if (row < 1 || row > boardSize || col < 1 || col > boardSize) {
    return null;
  }

  // All-squares numbering: every square playable, row-major
  if (allSquaresPlayable) {
    return (row - 1) * boardSize + col;
  }

  const isItalian = darkSquareBottomRight;

  // Verifica che la casella sia scura (giocabile)
  // Italian: (row + col) pari = scura
  // International: (row + col) dispari = scura
  const isBlackSquare = isItalian
    ? (row + col) % 2 === 0
    : (row + col) % 2 === 1;

  if (!isBlackSquare) {
    return null;
  }

  // Calcola il numero della casella
  const squaresPerRow = boardSize / 2;
  const squaresBeforeThisRow = (row - 1) * squaresPerRow;

  let posInRow: number;
  if (isItalian) {
    // Italian: righe dispari → colonne dispari, righe pari → colonne pari
    posInRow = row % 2 === 1 ? (col - 1) / 2 : (col - 2) / 2;
  } else {
    // International: righe dispari → colonne pari, righe pari → colonne dispari
    posInRow = row % 2 === 1 ? (col - 2) / 2 : (col - 1) / 2;
  }

  return squaresBeforeThisRow + posInRow + 1;
}

/**
 * Converti coordinate SVG (x, y) in numero di casella
 * @param boardSize Dimensione scacchiera (10 o 8, default 10)
 * @returns Square number o null se fuori dalla board o casella chiara
 */
export function svgCoordsToSquare(
  x: number,
  y: number,
  squareSize: number,
  boardSize: number = 10,
  darkSquareBottomRight: boolean = boardSize === 8,
  allSquaresPlayable: boolean = false
): Square | null {
  const col = Math.floor(x / squareSize) + 1;
  const row = Math.floor(y / squareSize) + 1;

  if (row < 1 || row > boardSize || col < 1 || col > boardSize) {
    return null;
  }

  return positionToSquare({ row, col }, boardSize, darkSquareBottomRight, allSquaresPlayable);
}

/**
 * Verifica se una casella è scura (giocabile)
 *
 * @param row Riga (1-indexed)
 * @param col Colonna (1-indexed)
 * @param boardSize Dimensione scacchiera (10 o 8, default 10)
 */
export function isPlayableSquare(row: number, col: number, boardSize: number = 10, darkSquareBottomRight: boolean = boardSize === 8): boolean {
  const isItalian = darkSquareBottomRight;
  // Italian: (row + col) pari = scura
  // International: (row + col) dispari = scura
  return isItalian
    ? (row + col) % 2 === 0
    : (row + col) % 2 === 1;
}
